// @ts-strict-ignore
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { LMCompactFormEvent, LMFieldBaseComponent, LMSubFormEvent } from '@field/lm-field-base.component';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { LMTextEvent } from '@field/text';
import { AppConstants, LMButtonType } from '@utils/app.constants';
import { LMModelChangedEvent } from '@form/model';
import { SohoTooltipDirective } from 'ids-enterprise-ng';

enum LMHyperlinkTypeDisplayEnum {
  MAIL_LINK = 0,
  PLAIN_LINK
}

@Component({
  selector:    'lm-hyperlink',
  templateUrl: './lm-hyperlink.component.html',
  styleUrls:   [ 'lm-hyperlink.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LMHyperlinkComponent extends LMFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() layoutObject: LMButtonLayout;
  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();

  @ViewChild(SohoTooltipDirective) tooltip: SohoTooltipDirective;

  public hyperlinkType: string = null;
  public resourceValue: string = null;
  public mailToHttpUrl = false;
  public valid = false;
  public textMessageRule: LMMessageRule

  public displayType: LMHyperlinkTypeDisplayEnum;
  public layoutDisplayType;
  public validSohoIcon: boolean;

  private isCompactForm = false;

  private isCreateReportMode = false;

  constructor(protected lmFieldServiceAdapter: LMFieldServiceAdapter) {
    super(lmFieldServiceAdapter);
    this.layoutDisplayType = LMHyperlinkTypeDisplayEnum;
  }

  ngOnInit() {
    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);

    // TODO: need to evaluate this again; re: LMCLIENT-26683:
    const spec: LMSpecField = this.compactFormDialogSpec;

    // for a compact form, use the spec and modelField based on this.field.fieldId
    if (!this.specField && spec) {
      this.isCompactForm = true;
      this.specField = spec;
      this.modelField = this.model.getField(this.field.fieldId);
    }

    this.textMessageRule = this.getTextMessageRule();

    this.valid = this.checkValidity();

    if (this.layoutObject.httpUrl && this.layoutObject.httpUrl.resourceValue.indexOf('mailto:') === 0) {
      this.mailToHttpUrl = true;
    }

    if (this.layoutObject.icon) {
      this.validSohoIcon = this.lmFieldServiceAdapter.lmThemeService.isValidSohoIcon(this.layoutObject.icon);
    }

    if (this.mailToHttpUrl) {
      this.displayType = this.layoutDisplayType.MAIL_LINK;

    } else {
      this.displayType = this.layoutDisplayType.PLAIN_LINK;

    }

    this.subscriptionArray.push(this.model.modelChanged
      .subscribe((modelChangedEvent: LMModelChangedEvent) => {
        if (modelChangedEvent.eventType === 'set') {
          // triggerValue changes to indicate the formatMessageRule pipe to fire
          // triggerValue is set in lm-field-base.component; do not set it here
          this.textMessageRule = this.getTextMessageRule();
        }

        this.valid = this.checkValidity();
      })
    );

    this.subscriptionArray.push(
      this.lmFieldServiceAdapter.lmFormService.isCreateReportMode$.subscribe(state => (this.isCreateReportMode = state, this.valid = this.checkValidity()))
    );
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  // duplicate code in lm-button
  // FYI.  Rich client does NOT check validity of a navigation button on a form
  checkValidity() {
    if (this.model?.isDrillAroundMode()) {
      return false;
    }

    if (this.isCreateReportMode) {
      return false;
    }

    const isLinkValidInArchiveMode = [ LMButtonType.ACTION, LMButtonType.RELATED, LMButtonType.FORMINVOKE ]
      .find( buttonType => buttonType.toString() === this.layoutObject?.type );
    if (this.lmFieldServiceAdapter.lmContextService.isDataModificationNotAllowed() && isLinkValidInArchiveMode) {
        return false;
    }

    let valid = true;

    // if (this.field.type === AppConstants.BUTTON_TYPE_NAVIGATION) {
    //   // TODO: LMCLIENT-21757, LMCLIENT-23380, LMCLIENT-25651; temp work around until LMRK-83694 is completed
    //   if (this.lmFieldServiceAdapter.lmFormService.isStepProcessForm(this.model.formSpec)) {
    //     return true;
    //   }
    //
    //   valid = !this.lmFieldServiceAdapter.lmConditionUtilService.isNavigationInvalid(this.layoutObject, this.model);
    // }

    if (this.layoutObject.validity) {
      valid = this.lmFieldServiceAdapter.lmConditionUtilService.isValid(this.layoutObject.validity as LMCondition[], this.model);
    }

    if (this.layoutObject.isStandardAction) {
      if (this.layoutObject.standardActionName !== AppConstants.STANDARD_SAVE) {
        // only save supported, disable all other standard actions
        valid = false;
      }
    }

    return valid;
  }

  onClickEvent(event) {
    this.busyIndicator = true;
    this.hyperlinkType = 'show-visited';

    if (this.isCompactForm) {
      this.fieldEvent.emit(new LMCompactFormEvent(event, this.field.fieldId, this.specField.label, this.specField.layout, this.layoutObject.moreSpecFields));

    } else if (this.isSubForm) {
      this.fieldEvent.emit(new LMSubFormEvent(event, this.field.fieldId, this.field.buttonSubForm.title, this.field.buttonSubForm));

    } else if (this.layoutObject.type === LMButtonType.SHOWEDITOR && this.modelField.isCompoundField) {
      const title = this.specFields[ this.layoutObject.fieldId ].label;
      const layout: LMFormLayout = { sections: this.layoutObject.layout as Array<LMFormLayoutSection> };
      this.fieldEvent.emit(new LMCompactFormEvent(event, this.field.fieldId, title, layout, this.layoutObject.moreSpecFields));

    } else {
      this.fieldEvent.emit(new LMTextEvent(event, this.field));
    }
    this.busyIndicator = false;
  }

  onMouseEnter() {
    if (this.layoutInfo.isContextFormOnSide) {
      this.tooltip.content = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.textMessageRule, this.model, this.specFields);
    }
  }

  private getTextMessageRule() {
    if (this.field) {
      if (this.field.hasOwnProperty('text')
        && this.field.text.hasOwnProperty('resourceValue')) {
        return this.field.text;

      } else if (this.field.hasOwnProperty('title')
        && this.field.title.hasOwnProperty('resourceValue')) {
        return this.field.title;
      }
    }

    return undefined;
  }
}
